import { useMemo, useState } from "react";
import { useSingleFlight } from "@/lib/manufacturing/useSingleFlight";
import type { MfgMaterial, MfgPlant } from "@/lib/manufacturingClient";
import { createManufacturingPurchaseOrder } from "@/lib/manufacturing/purchaseOrderClient";
import {
  newDraftLine,
  serializeDraftLines,
  type PoDraftLine,
} from "@/components/manufacturing/purchaseOrderDraftState";
import {
  attachCatalogMatch,
  canSubmitPlantMaterialRequest,
  filledPlantRequestLines,
} from "@/lib/manufacturing/plantMaterialRequest";
import {
  ghostBtnClass,
  inputClass,
  mfgPrimaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import { PlantMaterialRequestDictate } from "./PlantMaterialRequestDictate";
import { PlantMaterialRequestSimpleLines } from "./PlantMaterialRequestSimpleLines";

export function PlantMaterialRequestCompose(props: {
  enabled: boolean;
  plants: MfgPlant[];
  materials: MfgMaterial[];
  defaultPlantId?: string;
  onSubmitted: (poNumber: string) => void;
}) {
  const defaultPlant =
    props.defaultPlantId ||
    props.plants.find((p) => p.is_default)?.id ||
    props.plants[0]?.id ||
    "";
  const [plantId, setPlantId] = useState(defaultPlant);
  const [note, setNote] = useState("");
  const [lines, setLines] = useState<PoDraftLine[]>([newDraftLine()]);
  const [busy, setBusy] = useState(false);
  const flight = useSingleFlight();
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);
  const plantOptions = useMemo(() => props.plants, [props.plants]);
  const pid = plantId || defaultPlant;

  async function submit() {
    if (!pid) {
      setMsg({ text: "Select a plant.", ok: false });
      return;
    }
    const matched = filledPlantRequestLines(lines).map((line) =>
      attachCatalogMatch(line, props.materials, pid),
    );
    if (!canSubmitPlantMaterialRequest(note, matched)) {
      setMsg({ text: "Add a note or at least one item.", ok: false });
      return;
    }
    setBusy(true);
    setMsg(null);
    const res = await createManufacturingPurchaseOrder({
      plant_id: pid,
      origin: "plant_request",
      request_note: note.trim(),
      lines: serializeDraftLines(matched),
    });
    setBusy(false);
    if (!res.ok) {
      setMsg({ text: res.error, ok: false });
      return;
    }
    setLines([newDraftLine()]);
    setNote("");
    setMsg({ text: `Sent. Purchasing will see request ${res.data.order.po_number}.`, ok: true });
    props.onSubmitted(res.data.order.po_number);
  }

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold">Inventory request</h3>
        <p className="text-xs text-gray-600 dark:text-zinc-400">
          Say what you need — qty and item, or a freeform note. Purchasing picks vendors and
          issues the PO.
        </p>
      </div>
      {plantOptions.length > 1 ? (
        <label className="block text-xs">
          <span className="font-semibold">Plant</span>
          <select
            className={`${inputClass} mt-1 w-full`}
            value={pid}
            disabled={!props.enabled || busy}
            onChange={(e) => setPlantId(e.target.value)}
          >
            {plantOptions.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <PlantMaterialRequestDictate
        enabled={props.enabled}
        busy={busy}
        materials={props.materials}
        plantId={pid}
        onApplyLines={setLines}
        onApplyNote={(spoken) =>
          setNote((cur) => (cur.trim() ? `${cur.trim()}\n${spoken}` : spoken))
        }
      />
      <label className="block text-xs">
        <span className="font-semibold">Note</span>
        <textarea
          className={`${inputClass} mt-1 w-full`}
          rows={3}
          value={note}
          disabled={!props.enabled || busy}
          onChange={(e) => setNote(e.target.value)}
          placeholder="We're out of siding / need a pallet before Friday"
        />
      </label>
      <PlantMaterialRequestSimpleLines
        lines={lines}
        onChange={setLines}
        materials={props.materials}
        plantId={pid}
        enabled={props.enabled}
        busy={busy}
      />
      {msg ? (
        <p className={msg.ok ? "text-xs text-emerald-700 dark:text-emerald-300" : "text-xs text-rose-700"}>
          {msg.text}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className={mfgPrimaryBtnClass}
          disabled={!props.enabled || busy}
          onClick={() => void flight(() => submit())}
        >
          Send to purchasing
        </button>
        <button
          type="button"
          className={ghostBtnClass}
          disabled={busy}
          onClick={() => {
            setLines([newDraftLine()]);
            setNote("");
            setMsg(null);
          }}
        >
          Clear
        </button>
      </div>
    </div>
  );
}
